import { CommonModule } from '@angular/common';
import { trigger, transition, style, animate } from '@angular/animations';
import { Component, inject } from '@angular/core';
import { AuthService } from '../../services/auth/auth.service';
import { Router, RouterModule } from '@angular/router';
import { UserRole } from '../../core/models';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [CommonModule, RouterModule],
  template: `
    <nav
      *ngIf="authService.currentUserSig() as appUser"
      class="border-b border-gray-200 bg-white"
    >
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div class="flex h-16 justify-between">
          <div class="flex">
            <div
              class="flex flex-shrink-0 items-center flex-col text-center justify-center"
            >
              <img
                class="block h-8 w-auto lg:hidden"
                src="assets/logo-min.webp"
                alt="Columbus Logo"
              />
              <img
                class="hidden h-8 w-auto lg:block"
                src="assets/logo-min.webp"
                alt="Columbus Logo"
              />
              <p class="text-xs py-0.5 text-gray-600 font-extralight">
                {{ version }}
              </p>
            </div>
            <div class="hidden sm:-my-px sm:ml-6 sm:flex sm:space-x-8">
              <a
                routerLink="/dashboard"
                [ngStyle]="getStyleforRoute('/dashboard')"
                class="hover:border-gray-300 hover:text-gray-700 inline-flex items-center border-b-2 px-1 pt-1 text-sm font-medium"
                aria-current="page"
                >Dashboard</a
              >

              <ng-container *ngIf="authService.isAdmin()">
                <a
                  routerLink="/ships"
                  [ngStyle]="getStyleforRoute('/ships')"
                  class=" hover:border-gray-300 hover:text-gray-700 inline-flex items-center border-b-2 px-1 pt-1 text-sm font-medium"
                  >Ships</a
                >

                <a
                  routerLink="/resources"
                  [ngStyle]="getStyleforRoute('/resources')"
                  class="hover:border-gray-300 hover:text-gray-700 inline-flex items-center border-b-2 px-1 pt-1 text-sm font-medium"
                  >Guides</a
                >
                <a
                  routerLink="/tours"
                  [ngStyle]="getStyleforRoute('/tours')"
                  class="hover:border-gray-300 hover:text-gray-700 inline-flex items-center border-b-2 px-1 pt-1 text-sm font-medium"
                  >Tours</a
                >
                <a
                  routerLink="/cruise-manager"
                  [ngStyle]="getStyleforRoute('/cruise-manager')"
                  class="hover:border-gray-300 hover:text-gray-700 inline-flex items-center border-b-2 px-1 pt-1 text-sm font-medium"
                  >Cruise Manager</a
                >
                <a
                  routerLink="/import"
                  [ngStyle]="getStyleforRoute('/import')"
                  class="hover:border-gray-300 hover:text-gray-700 inline-flex items-center border-b-2 px-1 pt-1 text-sm font-medium"
                  >Import</a
                >
                <a
                  routerLink="/archive"
                  [ngStyle]="getStyleforRoute('/archive')"
                  class="hover:border-gray-300 hover:text-gray-700 inline-flex items-center border-b-2 px-1 pt-1 text-sm font-medium"
                  >Archive</a
                >
              </ng-container>
            </div>
          </div>
          <div class="hidden sm:ml-6 sm:flex sm:items-center">
            <!-- Profile dropdown -->
            <div class="relative ml-3">
              <div>
                <button
                  type="button"
                  (click)="toggleMenu()"
                  class="flex max-w-xs items-center rounded-full bg-white text-sm focus:outline-none focus:ring-2 focus:ring-columbus-blue-500 focus:ring-offset-2"
                  id="user-menu-button"
                  aria-expanded="false"
                  aria-haspopup="true"
                >
                  <span class="sr-only">Open user menu</span>
                  <div
                    class="px-2 py-1 bg-columbus-gold-200  text-columbus-blue-950 rounded-md font-bold flex justify-center text-center items-center"
                  >
                    {{ appUser.username }}
                  </div>
                </button>
              </div>

              <!--
              Dropdown menu, show/hide based on menu state.

              Entering: "transition ease-out duration-200"
                From: "transform opacity-0 scale-95"
                To: "transform opacity-100 scale-100"
              Leaving: "transition ease-in duration-75"
                From: "transform opacity-100 scale-100"
                To: "transform opacity-0 scale-95"
            -->
              <div
                [@opacityScale]
                *ngIf="isMenu"
                class="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                role="menu"
                aria-orientation="vertical"
                aria-labelledby="user-menu-button"
                tabindex="-1"
              >
                <a
                  (click)="onSignOut()"
                  class="block px-4 py-2 text-sm text-gray-700"
                  role="menuitem"
                  tabindex="-1"
                  id="user-menu-item-2"
                  >Sign out</a
                >
              </div>
            </div>
          </div>
          <div class="-mr-2 flex items-center sm:hidden">
            <!-- Mobile menu button -->
            <button
              type="button"
              (click)="toggleMenu()"
              class="inline-flex items-center justify-center rounded-md bg-white p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-columbus-blue-500 focus:ring-offset-2"
              aria-controls="mobile-menu"
              aria-expanded="false"
            >
              <span class="sr-only">Open main menu</span>
              <!-- Menu open: "hidden", Menu closed: "block" -->
              <svg
                *ngIf="!isMenu"
                class="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
                />
              </svg>
              <!-- Menu open: "block", Menu closed: "hidden" -->
              <svg
                *ngIf="isMenu"
                class="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Mobile menu, show/hide based on menu state. -->
      <div *ngIf="isMenu" class="sm:hidden" id="mobile-menu">
        <div class="space-y-1 pt-2 pb-3">
          <!-- Current: "border-columbus-blue-500 bg-columbus-blue-50 text-columbus-blue-700", Default: "border-transparent text-gray-600 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800" -->
          <a
            routerLink="/dashboard"
            [ngStyle]="getStyleforRoute('/dashboard')"
            class="hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800  block border-l-4 py-2 pl-3 pr-4 text-base font-medium"
            aria-current="page"
            >Dashboard</a
          >
          <ng-container *ngIf="authService.isAdmin()">
            <a
              routerLink="/ships"
              [ngStyle]="getStyleforRoute('/ships')"
              class=" hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800 block border-l-4 py-2 pl-3 pr-4 text-base font-medium"
              >Ships</a
            >

            <a
              routerLink="/resources"
              [ngStyle]="getStyleforRoute('/resources')"
              class=" hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800 block border-l-4 py-2 pl-3 pr-4 text-base font-medium"
              >Guides</a
            >
            <a
              routerLink="/tours"
              [ngStyle]="getStyleforRoute('/tours')"
              class=" hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800 block border-l-4 py-2 pl-3 pr-4 text-base font-medium"
              >Tours</a
            >
            <a
              routerLink="/cruise-manager"
              [ngStyle]="getStyleforRoute('/cruise-manager')"
              class=" hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800 block border-l-4 py-2 pl-3 pr-4 text-base font-medium"
              >Cruise Manager</a
            >
            <a
              routerLink="/import"
              [ngStyle]="getStyleforRoute('/import')"
              class=" hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800 block border-l-4 py-2 pl-3 pr-4 text-base font-medium"
              >Import</a
            >
            <a
              routerLink="/archive"
              [ngStyle]="getStyleforRoute('/archive')"
              class=" hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800 block border-l-4 py-2 pl-3 pr-4 text-base font-medium"
              >Archive</a
            >
          </ng-container>
        </div>
        <div class="border-t border-gray-200 pt-4 pb-3">
          <div class="flex items-center px-4">
            <div class="">
              <div class="text-base font-medium text-gray-800">
                {{ appUser.username }}
              </div>
              <div class="text-sm font-medium text-gray-500">
                {{ appUser.email }}
              </div>
            </div>
          </div>
          <div class="mt-3 space-y-1">
            <a
              (click)="onSignOut()"
              class="block px-4 py-2 text-base font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800"
              >Sign out</a
            >
          </div>
        </div>
      </div>
    </nav>
  `,
  styles: ``,
  animations: [
    trigger('opacityScale', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(.95)' }),
        animate('200ms ease-out', style({ opacity: 1, transform: 'scale(1)' })),
      ]),
      transition(':leave', [
        style({ opacity: 1, transform: 'scale(1)' }),
        animate('75ms ease-in', style({ opacity: 0, transform: 'scale(.95)' })),
      ]),
    ]),
  ],
})
export class NavbarComponent {
  protected authService = inject(AuthService);
  private router = inject(Router);
  isMenu = false;
  userRole = UserRole;
  version = '0.0.0.0';
  constructor() {
    this.version = environment.version;
  }

  toggleMenu() {
    this.isMenu = !this.isMenu;
  }

  onSignOut() {
    this.authService.logout().subscribe({
      next: () => {
        this.toggleMenu();
        this.router.navigateByUrl('/home');
      },
      error: (err) => {
        console.error(err);
        this.toggleMenu();
      },
    });
  }

  getStyleforRoute(path: string): { [key: string]: string } {
    const isActive = this.router.url.startsWith(path);

    return isActive
      ? { borderColor: 'rgb(59, 130, 246)', color: 'rgb(17, 24, 39)' }
      : { borerColor: 'transparent', color: 'rgb(107, 114, 128)' };
  }
}
